//
// Shared constants + digest helper for replay base states (the emulator state a
// save-loaded session's replay starts from). Browser-safe: WebCrypto only.

/** Canonical (uncompressed) state ceiling — enforced after every gunzip. */
export const MAX_BASE_STATE_BYTES = 8 * 1024 * 1024;
export const MAX_N64_BASE_STATE_BYTES = 24 * 1024 * 1024;
/** Compressed-body ceiling for the upload wire. gzip of incompressible input can
 *  be marginally LARGER than the input, so a valid 8 MB state must not be
 *  rejected at the wire before it is even decompressed. */
export const MAX_WIRE_BYTES = Math.ceil(MAX_BASE_STATE_BYTES * 1.001) + 128;
export const MAX_N64_WIRE_BYTES = Math.ceil(MAX_N64_BASE_STATE_BYTES * 1.001) + 128;

export function baseStateByteLimitForBuild(sourceCoreBuild) {
  return typeof sourceCoreBuild === 'string' && sourceCoreBuild.endsWith(':mupen64plus_next')
    && isValidClientCoreBuildId(sourceCoreBuild) ? MAX_N64_BASE_STATE_BYTES : MAX_BASE_STATE_BYTES;
}

export function baseStateWireLimitForBuild(sourceCoreBuild) {
  return baseStateByteLimitForBuild(sourceCoreBuild) === MAX_N64_BASE_STATE_BYTES
    ? MAX_N64_WIRE_BYTES : MAX_WIRE_BYTES;
}
export const MAX_BASE_STATES_PER_USER = 50;
/** Release paths never touch a row whose lease is in the future; every
 *  successful PUT refreshes it. */
export const BASE_STATE_LEASE_MS = 60 * 60 * 1000;
/** The weekly object sweep only considers blobs older than this. */
export const BASE_STATE_OBJECT_SWEEP_MIN_AGE_MS = 24 * 60 * 60 * 1000;
/** Extra grace when comparing provider object timestamps with the DB clock. */
export const OBJECT_CLOCK_SKEW_ALLOWANCE_MS = 60 * 60 * 1000;

/** Wall-clock ceiling for a single base-state blob op (download / upload / delete)
 *  on the web tier and the Function. A hung storage call rejects at this bound
 *  instead of pinning a request, a worker, or an open DB transaction. Chosen well
 *  under the 30 s per-statement DB budget and the client's 30 s network deadline
 *  so a stuck blob fails before the surrounding operation's own limits fire. */
export const BASE_STATE_STORAGE_TIMEOUT_MS = 15_000;

const CLIENT_CORE_BUILD_RE = /^retroplay@[A-Za-z0-9][A-Za-z0-9._+-]{0,63}:[A-Za-z0-9_][A-Za-z0-9_-]{0,63}$/;

/** Exact grammar emitted by the browser core wrappers and accepted as stored
 * producer provenance. Consumer-only ids (for example `wasm:<digest>`) are not
 * valid producer ids. */
export function isValidClientCoreBuildId(value) {
  return typeof value === 'string' && CLIENT_CORE_BUILD_RE.test(value);
}

export class BaseStateError extends Error {
  constructor(code, {
    message = code,
    transient = false,
    operation = null,
    path = null,
    deadlineContext = null,
    cause,
  } = {}) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'BaseStateError';
    this.code = code;
    this.transient = !!transient;
    this.terminal = !this.transient;
    this.operation = operation;
    this.path = path;
    this.deadlineContext = deadlineContext;
  }
}

export class DeadlineExceededError extends BaseStateError {
  constructor({ operation = null, path = null, deadlineContext = null, cause } = {}) {
    super('base_state_deadline_exceeded', {
      message: 'base-state operation deadline exceeded',
      transient: true,
      operation,
      path,
      deadlineContext,
      cause,
    });
    this.name = 'DeadlineExceededError';
  }
}

/**
 * One absolute deadline shared by every stage of an operation. The signal is
 * suitable for fetch and Azure SDK calls; DB timeouts are derived from the same
 * remaining budget so a sequence of individually-bounded statements cannot
 * outlive the request that owns them.
 */
export class OperationDeadline {
  constructor(totalMs, { signal: parent, operation = null } = {}) {
    if (!Number.isFinite(totalMs) || totalMs <= 0) {
      throw new TypeError('OperationDeadline totalMs must be positive');
    }
    this.totalMs = Math.ceil(totalMs);
    this.startedAt = Date.now();
    this.deadlineAt = this.startedAt + this.totalMs;
    this.operation = operation;
    this._controller = new AbortController();
    this._parent = parent || null;
    this._onParentAbort = () => {
      const reason = parent?.reason instanceof Error
        ? parent.reason
        : new BaseStateError('base_state_aborted', {
            transient: true,
            operation: this.operation,
            deadlineContext: this.context(),
            cause: parent?.reason,
          });
      this._controller.abort(reason);
    };
    if (parent?.aborted) this._onParentAbort();
    else parent?.addEventListener?.('abort', this._onParentAbort, { once: true });
    this._timer = setTimeout(() => {
      this._controller.abort(new DeadlineExceededError({
        operation: this.operation,
        deadlineContext: this.context(),
      }));
    }, this.totalMs);
    this._timer?.unref?.();
  }

  get signal() { return this._controller.signal; }

  remainingMs() { return Math.max(0, this.deadlineAt - Date.now()); }

  context() {
    return {
      totalMs: this.totalMs,
      startedAt: this.startedAt,
      deadlineAt: this.deadlineAt,
      remainingMs: this.remainingMs(),
    };
  }

  throwIfExpired({ operation = this.operation, path = null } = {}) {
    if (!this.signal.aborted && this.remainingMs() > 0) return;
    const reason = this.signal.reason;
    if (reason instanceof Error) throw reason;
    throw new DeadlineExceededError({ operation, path, deadlineContext: this.context(), cause: reason });
  }

  dbTimeoutMs(cap) {
    if (!Number.isFinite(cap) || cap <= 0) throw new TypeError('DB timeout cap must be positive');
    this.throwIfExpired();
    return Math.max(1, Math.min(Math.ceil(cap), this.remainingMs()));
  }

  abort(reason = new BaseStateError('base_state_aborted', {
    transient: true,
    operation: this.operation,
    deadlineContext: this.context(),
  })) {
    if (!this.signal.aborted) this._controller.abort(reason);
  }

  dispose() {
    clearTimeout(this._timer);
    this._timer = null;
    this._parent?.removeEventListener?.('abort', this._onParentAbort);
    this._parent = null;
  }
}

/** Await a promise while observing an AbortSignal without losing its cause. */
export function waitForAbortable(promise, signal, {
  operation = null,
  path = null,
  deadlineContext = null,
} = {}) {
  if (!signal) return promise;
  if (signal.aborted) {
    return Promise.reject(signal.reason instanceof Error
      ? signal.reason
      : new BaseStateError('base_state_aborted', {
          transient: true, operation, path, deadlineContext, cause: signal.reason,
        }));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(signal.reason instanceof Error
      ? signal.reason
      : new BaseStateError('base_state_aborted', {
          transient: true, operation, path, deadlineContext, cause: signal.reason,
        }));
    signal.addEventListener('abort', onAbort, { once: true });
    Promise.resolve(promise).then(
      (value) => { signal.removeEventListener('abort', onAbort); resolve(value); },
      (error) => { signal.removeEventListener('abort', onAbort); reject(error); },
    );
  });
}

/**
 * Collect a ReadableStream, reader, or async iterable into one preallocated
 * bounded buffer. The allocation is the maximum retained payload: chunks are
 * copied into it as they arrive, so there is no chunks-plus-concatenation peak.
 */
export async function collectBounded(source, maxBytes, signal) {
  if (!Number.isSafeInteger(maxBytes) || maxBytes < 0) {
    throw new TypeError('collectBounded maxBytes must be a non-negative safe integer');
  }
  const output = new Uint8Array(maxBytes);
  let length = 0;
  let reader = null;
  let ownsReader = false;
  let iterator = null;
  let failed = true;
  const copy = (chunk) => {
    const bytes = chunk instanceof Uint8Array
      ? chunk
      : chunk instanceof ArrayBuffer
        ? new Uint8Array(chunk)
        : ArrayBuffer.isView(chunk)
          ? new Uint8Array(chunk.buffer, chunk.byteOffset, chunk.byteLength)
          : null;
    if (!bytes) throw new TypeError('collectBounded source yielded a non-byte chunk');
    if (length + bytes.byteLength > maxBytes) {
      throw new BaseStateError('base_state_too_large', {
        operation: 'collect',
        transient: false,
      });
    }
    output.set(bytes, length);
    length += bytes.byteLength;
  };

  try {
    if (source?.getReader) {
      reader = source.getReader();
      ownsReader = true;
    } else if (source?.read && !source?.[Symbol.asyncIterator]) {
      reader = source;
    }

    if (reader) {
      for (;;) {
        const step = await waitForAbortable(reader.read(), signal, { operation: 'stream_read' });
        if (step.done) break;
        copy(step.value);
      }
    } else if (source?.[Symbol.asyncIterator]) {
      iterator = source[Symbol.asyncIterator]();
      for (;;) {
        const step = await waitForAbortable(iterator.next(), signal, { operation: 'stream_read' });
        if (step.done) break;
        copy(step.value);
      }
    } else if (source != null) {
      copy(source);
    }
    failed = false;
    return output.subarray(0, length);
  } finally {
    if (reader && failed) {
      const cancel = Promise.resolve().then(() => reader.cancel?.()).catch(() => {});
      if (signal) await waitForAbortable(cancel, signal, { operation: 'stream_cancel' }).catch(() => {});
      else cancel.catch(() => {});
    }
    if (reader && ownsReader) {
      try { reader.releaseLock(); } catch { /* already released/closed */ }
    }
    if (iterator && failed) {
      const close = Promise.resolve().then(() => iterator.return?.()).catch(() => {});
      if (signal) await waitForAbortable(close, signal, { operation: 'stream_cancel' }).catch(() => {});
      else await close;
    }
  }
}

/**
 * Reject if `promise` has not settled within `ms`. The timer is always cleared,
 * so a settled op leaves nothing pending. The rejection carries a stable
 * `code: 'base_state_storage_timeout'`, `transient: true`, and the elapsed
 * bound so callers can treat a stall as a retryable infra hiccup and log it.
 *
 * Compatibility shim for legacy callers. New storage paths create one
 * OperationDeadline before starting the SDK call and pass its signal into the
 * SDK; a promise that was already created cannot be retroactively cancelled.
 */
export function withStorageTimeout(promise, ms = BASE_STATE_STORAGE_TIMEOUT_MS, label = 'storage') {
  const deadline = new OperationDeadline(ms, { operation: label });
  return waitForAbortable(promise, deadline.signal, {
    operation: label,
    deadlineContext: deadline.context(),
  }).catch((error) => {
    if (error?.code === 'base_state_deadline_exceeded') {
      error.code = 'base_state_storage_timeout';
      error.message = `base state ${label} timed out after ${ms}ms`;
    }
    throw error;
  }).finally(() => deadline.dispose());
}

const HEX = Array.from({ length: 256 }, (_, v) => v.toString(16).padStart(2, '0'));

/** Lowercase hex SHA-256 of `bytes` (Uint8Array or ArrayBuffer). */
export async function sha256Hex(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', u8));
  let hex = '';
  for (const b of digest) hex += HEX[b];
  return hex;
}
